/**
 * @fileoverview Invoice Create Page - Accounts Receivable
 * @task B.INV.6 - Invoice builder with live preview
 *
 * Full-featured invoice builder with real-time preview for investor demos
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@components/ui/button';
import { InvoicePreview, type InvoicePreviewData } from '@components/invoices/InvoicePreview';
import { generateId } from '@lib/utils';
import { useMarket } from '@contexts/MarketContext';
import {
  invoicesApi,
  type Customer,
  type InvoiceLineItem,
} from '@lib/demo-data';

interface FormLineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
}

interface InvoiceFormData {
  customerId: string;
  invoiceNumber: string;
  issueDate: string;
  dueDate: string;
  paymentTerms: string;
  notes: string;
  discount: number;
  taxRate: number;
  lineItems: FormLineItem[];
}

const paymentTermsOptionsEN = [
  { value: '0', label: 'Due on Receipt', days: 0 },
  { value: '15', label: 'Net 15', days: 15 },
  { value: '30', label: 'Net 30', days: 30 },
  { value: '45', label: 'Net 45', days: 45 },
  { value: '60', label: 'Net 60', days: 60 },
];

const paymentTermsOptionsBR = [
  { value: '0', label: 'À Vista', days: 0 },
  { value: '15', label: '15 Dias', days: 15 },
  { value: '30', label: '30 Dias', days: 30 },
  { value: '45', label: '45 Dias', days: 45 },
  { value: '60', label: '60 Dias', days: 60 },
];

export function InvoiceCreatePage(): JSX.Element {
  const navigate = useNavigate();
  const { isBrazil, formatCurrency: marketFormatCurrency } = useMarket();
  const paymentTermsOptions = isBrazil ? paymentTermsOptionsBR : paymentTermsOptionsEN;

  // Customers
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  // Form state
  const [formData, setFormData] = useState<InvoiceFormData>({
    customerId: '',
    invoiceNumber: '',
    issueDate: new Date().toISOString().split('T')[0] ?? '',
    dueDate: '',
    paymentTerms: '30',
    notes: '',
    discount: 0,
    taxRate: 8.25,
    lineItems: [{ id: generateId(), description: '', quantity: 1, unitPrice: 0 }],
  });

  // Loading/saving state
  const [isSaving, setIsSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(true);

  // Fetch customers and next invoice number
  useEffect(() => {
    const loadInitialData = async (): Promise<void> => {
      try {
        const [customersData, nextNumber] = await Promise.all([
          invoicesApi.getCustomers(),
          invoicesApi.getNextInvoiceNumber(),
        ]);
        setCustomers(customersData);
        setFormData((prev) => ({ ...prev, invoiceNumber: nextNumber }));
      } catch (error) {
        console.error('Error loading initial data:', error);
      }
    };
    void loadInitialData();
  }, []);

  // Update due date when issue date or payment terms change
  useEffect(() => {
    if (formData.issueDate && formData.paymentTerms) {
      const issueDate = new Date(formData.issueDate);
      const days = parseInt(formData.paymentTerms, 10);
      issueDate.setDate(issueDate.getDate() + days);
      const dueDate = issueDate.toISOString().split('T')[0] ?? '';
      setFormData((prev) => ({ ...prev, dueDate }));
    }
  }, [formData.issueDate, formData.paymentTerms]);

  // Update selected customer when customerId changes
  useEffect(() => {
    if (formData.customerId) {
      const customer = customers.find((c) => c.id === formData.customerId) ?? null;
      setSelectedCustomer(customer);

      // Update payment terms based on customer default
      if (customer?.paymentTerms) {
        const matchingTerm = paymentTermsOptions.find((t) => t.days === customer.paymentTerms);
        if (matchingTerm) {
          setFormData((prev) => ({ ...prev, paymentTerms: matchingTerm.value }));
        }
      }
    } else {
      setSelectedCustomer(null);
    }
  }, [formData.customerId, customers]);

  // Handle form field changes
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>): void => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
    },
    []
  );

  // Handle line item changes
  const handleLineItemChange = useCallback(
    (id: string, field: keyof FormLineItem, value: string | number): void => {
      setFormData((prev) => ({
        ...prev,
        lineItems: prev.lineItems.map((item) =>
          item.id === id ? { ...item, [field]: value } : item
        ),
      }));
    },
    []
  );

  // Add line item
  const addLineItem = useCallback((): void => {
    setFormData((prev) => ({
      ...prev,
      lineItems: [...prev.lineItems, { id: generateId(), description: '', quantity: 1, unitPrice: 0 }],
    }));
  }, []);

  // Remove line item
  const removeLineItem = useCallback((id: string): void => {
    setFormData((prev) => ({
      ...prev,
      lineItems: prev.lineItems.length > 1 ? prev.lineItems.filter((item) => item.id !== id) : prev.lineItems,
    }));
  }, []);

  // Calculate totals
  const { subtotal, discountAmount, taxAmount, total } = useMemo(() => {
    const sub = formData.lineItems.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
    const disc = sub * (formData.discount / 100);
    const taxable = sub - disc;
    const tax = taxable * (formData.taxRate / 100);
    return {
      subtotal: sub,
      discountAmount: disc,
      taxAmount: tax,
      total: taxable + tax,
    };
  }, [formData.lineItems, formData.discount, formData.taxRate]);

  // Convert form line items to preview format
  const previewLineItems: InvoiceLineItem[] = useMemo(
    () =>
      formData.lineItems.map((item) => ({
        id: item.id,
        description: item.description || 'Untitled item',
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        amount: item.quantity * item.unitPrice,
      })),
    [formData.lineItems]
  );

  // Preview data
  const previewData: InvoicePreviewData = useMemo(
    () => ({
      invoiceNumber: formData.invoiceNumber,
      issueDate: formData.issueDate,
      dueDate: formData.dueDate,
      customer: selectedCustomer,
      lineItems: previewLineItems,
      notes: formData.notes,
      discount: formData.discount,
      taxRate: formData.taxRate,
    }),
    [formData, selectedCustomer, previewLineItems]
  );

  // Save as draft
  const handleSaveDraft = async (): Promise<void> => {
    setIsSaving(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 500));
      console.log('Saved as draft:', formData);
      navigate('/accounts-receivable/invoices');
    } catch (error) {
      console.error('Error saving draft:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Send invoice
  const handleSendInvoice = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 800));
      console.log('Sending invoice:', formData);
      navigate('/accounts-receivable/invoices');
    } catch (error) {
      console.error('Error sending invoice:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Page Header */}
      <header className="sticky top-0 z-40 border-b bg-card/95 backdrop-blur">
        <div className="container flex h-16 items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/accounts-receivable/invoices"
              className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
            >
              <svg className="mr-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              {isBrazil ? 'Voltar' : 'Back'}
            </Link>
            <div className="h-6 w-px bg-border" />
            <div>
              <h1 className="text-xl font-bold">{isBrazil ? 'Criar NF-e' : 'Create Invoice'}</h1>
              <p className="text-sm text-muted-foreground">{isBrazil ? 'Crie e envie notas fiscais para seus clientes' : 'Build and send invoices to your customers'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPreview(!showPreview)}
              className="hidden lg:flex"
            >
              {showPreview ? (isBrazil ? 'Ocultar Prévia' : 'Hide Preview') : (isBrazil ? 'Mostrar Prévia' : 'Show Preview')}
            </Button>
          </div>
        </div>
      </header>

      <main className="container py-6">
        <div className={`grid gap-6 ${showPreview ? 'lg:grid-cols-2' : 'max-w-3xl mx-auto'}`}>
          {/* Form Panel */}
          <div className="space-y-6">
            <form onSubmit={(e) => void handleSendInvoice(e)}>
              {/* Customer Selection */}
              <div className="rounded-lg border bg-card p-6 mb-6">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">{isBrazil ? 'Cliente' : 'Customer'}</h3>
                <select
                  name="customerId"
                  value={formData.customerId}
                  onChange={handleChange}
                  required
                  className="h-11 w-full rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">{isBrazil ? 'Selecione um cliente...' : 'Select a customer...'}</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name}
                    </option>
                  ))}
                </select>
                {selectedCustomer && (
                  <div className="mt-3 p-3 rounded-md bg-muted/50 text-sm">
                    <p className="font-medium">{selectedCustomer.name}</p>
                    <p className="text-muted-foreground">{selectedCustomer.address}</p>
                    <p className="text-muted-foreground">{selectedCustomer.email}</p>
                  </div>
                )}
              </div>

              {/* Invoice Details */}
              <div className="rounded-lg border bg-card p-6 mb-6">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
                  {isBrazil ? 'Detalhes da NF-e' : 'Invoice Details'}
                </h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium mb-1.5">{isBrazil ? 'Número da NF-e' : 'Invoice Number'}</label>
                    <input
                      type="text"
                      name="invoiceNumber"
                      value={formData.invoiceNumber}
                      onChange={handleChange}
                      className="h-10 w-full rounded-md border bg-background px-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring"
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">{isBrazil ? 'Condições de Pagamento' : 'Payment Terms'}</label>
                    <select
                      name="paymentTerms"
                      value={formData.paymentTerms}
                      onChange={handleChange}
                      className="h-10 w-full rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      {paymentTermsOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">{isBrazil ? 'Data de Emissão' : 'Issue Date'}</label>
                    <input
                      type="date"
                      name="issueDate"
                      value={formData.issueDate}
                      onChange={handleChange}
                      required
                      className="h-10 w-full rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">{isBrazil ? 'Vencimento' : 'Due Date'}</label>
                    <input
                      type="date"
                      name="dueDate"
                      value={formData.dueDate}
                      onChange={handleChange}
                      required
                      className="h-10 w-full rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                </div>
              </div>

              {/* Line Items */}
              <div className="rounded-lg border bg-card p-6 mb-6">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
                  {isBrazil ? 'Itens' : 'Line Items'}
                </h3>
                <div className="space-y-3">
                  {formData.lineItems.map((item) => (
                    <div key={item.id} className="flex gap-3 items-start">
                      <div className="flex-1">
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => handleLineItemChange(item.id, 'description', e.target.value)}
                          placeholder={isBrazil ? 'Descrição' : 'Description'}
                          className="h-10 w-full rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                        />
                      </div>
                      <div className="w-20">
                        <input
                          type="number"
                          value={item.quantity || ''}
                          onChange={(e) => handleLineItemChange(item.id, 'quantity', Number(e.target.value) || 1)}
                          min="1"
                          placeholder={isBrazil ? 'Qtd' : 'Qty'}
                          className="h-10 w-full rounded-md border bg-background px-3 text-sm text-center focus:outline-none focus:ring-2 focus:ring-ring"
                        />
                      </div>
                      <div className="w-28">
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">{isBrazil ? 'R$' : '$'}</span>
                          <input
                            type="number"
                            value={item.unitPrice || ''}
                            onChange={(e) => handleLineItemChange(item.id, 'unitPrice', Number(e.target.value) || 0)}
                            min="0"
                            step="0.01"
                            placeholder={isBrazil ? 'Preço' : 'Rate'}
                            className="h-10 w-full rounded-md border bg-background pl-7 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                          />
                        </div>
                      </div>
                      <div className="w-24 text-right">
                        <p className="h-10 flex items-center justify-end font-medium text-sm">
                          {marketFormatCurrency(item.quantity * item.unitPrice)}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeLineItem(item.id)}
                        disabled={formData.lineItems.length === 1}
                        className="h-10 w-10 flex items-center justify-center rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={addLineItem}
                  className="mt-4 inline-flex items-center text-sm text-primary font-medium hover:underline"
                >
                  <svg className="mr-1.5 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  {isBrazil ? 'Adicionar Item' : 'Add Line Item'}
                </button>
              </div>

              {/* Totals */}
              <div className="rounded-lg border bg-card p-6 mb-6">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium mb-1.5">{isBrazil ? 'Desconto (%)' : 'Discount (%)'}</label>
                      <input
                        type="number"
                        name="discount"
                        value={formData.discount}
                        onChange={handleChange}
                        min="0"
                        max="100"
                        step="0.01"
                        className="h-10 w-full rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5">{isBrazil ? 'Alíquota de Imposto (%)' : 'Tax Rate (%)'}</label>
                      <input
                        type="number"
                        name="taxRate"
                        value={formData.taxRate}
                        onChange={handleChange}
                        min="0"
                        max="100"
                        step="0.01"
                        className="h-10 w-full rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{isBrazil ? 'Subtotal' : 'Subtotal'}</span>
                      <span className="font-medium">{marketFormatCurrency(subtotal)}</span>
                    </div>
                    {formData.discount > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>{isBrazil ? `Desconto (${formData.discount}%)` : `Discount (${formData.discount}%)`}</span>
                        <span>-{marketFormatCurrency(discountAmount)}</span>
                      </div>
                    )}
                    {formData.taxRate > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{isBrazil ? `Impostos (${formData.taxRate}%)` : `Tax (${formData.taxRate}%)`}</span>
                        <span>{marketFormatCurrency(taxAmount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between border-t pt-2 text-lg">
                      <span className="font-semibold">{isBrazil ? 'Total' : 'Total'}</span>
                      <span className="font-bold">{marketFormatCurrency(total)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="rounded-lg border bg-card p-6 mb-6">
                <label className="block text-sm font-medium mb-1.5">{isBrazil ? 'Observações / Termos' : 'Notes / Terms'}</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows={3}
                  placeholder={isBrazil ? 'Adicione observações ou condições de pagamento para o cliente...' : 'Add any notes or payment terms for the customer...'}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                />
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3">
                <Link to="/accounts-receivable/invoices">
                  <Button variant="outline" type="button">
                    {isBrazil ? 'Cancelar' : 'Cancel'}
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => void handleSaveDraft()}
                  disabled={isSaving}
                >
                  {isSaving ? (isBrazil ? 'Salvando...' : 'Saving...') : (isBrazil ? 'Salvar Rascunho' : 'Save Draft')}
                </Button>
                <Button type="submit" disabled={isSaving || !formData.customerId}>
                  {isSaving ? (isBrazil ? 'Enviando...' : 'Sending...') : (isBrazil ? 'Enviar NF-e' : 'Send Invoice')}
                </Button>
              </div>
            </form>
          </div>

          {/* Preview Panel */}
          {showPreview && (
            <div className="hidden lg:block sticky top-24">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                  {isBrazil ? 'Prévia em Tempo Real' : 'Live Preview'}
                </h3>
                <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                  {isBrazil ? 'Atualiza enquanto você digita' : 'Updates as you type'}
                </span>
              </div>
              <div className="max-h-[calc(100vh-12rem)] overflow-y-auto rounded-lg shadow-lg">
                <InvoicePreview data={previewData} isBrazil={isBrazil} />
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
